import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ShareMode = "private" | "account" | "public";
export type SharePermission = "manage" | "write" | "readcopy" | "read";

export interface SharedUser {
  userId: number;
  email: string;
  displayName: string;
  permission: SharePermission;
  grantedAt?: string;
}

export interface SessionShareConfig {
  id: number;
  shareMode: ShareMode;
  shareToken: string;
  defaultPermission: SharePermission;
  shareUrl: string | null;
  users: SharedUser[];
  createdAt: string;
  updatedAt: string;
}

export interface SharedWithMeSession {
  guid: string;
  name: string;
  color: string;
  shareMode: ShareMode;
  shareToken: string;
  permission: SharePermission;
  ownerName: string;
  ownerEmail: string;
  updatedAt: string;
}

interface SharingState {
  // Share configs keyed by session guid
  shareConfigs: Record<string, SessionShareConfig>;
  // Sessions shared with the current user
  sharedWithMe: SharedWithMeSession[];
  // Loading states
  loadingShares: Set<string>;
  // Currently open share dialog session id
  shareDialogSessionId: string | null;

  // Actions
  fetchShareConfig: (
    sessionGuid: string,
    getToken: () => Promise<string | null>,
  ) => Promise<SessionShareConfig | null>;
  updateShareMode: (
    sessionGuid: string,
    shareMode: ShareMode,
    defaultPermission: SharePermission,
    getToken: () => Promise<string | null>,
  ) => Promise<void>;
  addSharedUser: (
    sessionGuid: string,
    email: string,
    permission: SharePermission,
    getToken: () => Promise<string | null>,
  ) => Promise<SharedUser | null>;
  updateUserPermission: (
    sessionGuid: string,
    userId: number,
    permission: SharePermission,
    getToken: () => Promise<string | null>,
  ) => Promise<void>;
  removeSharedUser: (
    sessionGuid: string,
    userId: number,
    getToken: () => Promise<string | null>,
  ) => Promise<void>;
  removeSharing: (
    sessionGuid: string,
    getToken: () => Promise<string | null>,
  ) => Promise<void>;
  fetchSharedWithMe: (getToken: () => Promise<string | null>) => Promise<void>;
  openShareDialog: (sessionId: string) => void;
  closeShareDialog: () => void;
  getSessionShareMode: (sessionGuid: string) => ShareMode;
}

const API_BASE =
  import.meta.env.VITE_API_URL ||
  (typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? `https://api.${window.location.hostname.replace(/^www\./, "")}`
    : "http://localhost:3001");

async function apiFetch(
  path: string,
  getToken: () => Promise<string | null>,
  options: RequestInit = {},
) {
  const token = await getToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string>),
  };
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Request failed" }));
    throw new Error(err.error || "Request failed");
  }
  return res.json();
}

export const useSharingStore = create<SharingState>()(
  persist(
    (set, get) => ({
      shareConfigs: {},
      sharedWithMe: [],
      loadingShares: new Set(),
      shareDialogSessionId: null,

      fetchShareConfig: async (sessionGuid, getToken) => {
        set((state) => ({
          loadingShares: new Set(state.loadingShares).add(sessionGuid),
        }));
        try {
          const data = await apiFetch(
            `/api/sessions/${sessionGuid}/share`,
            getToken,
          );
          set((state) => ({
            shareConfigs: {
              ...state.shareConfigs,
              [sessionGuid]: data.share,
            },
          }));
          return data.share;
        } catch (err) {
          console.error("Failed to fetch share config:", err);
          // Create a default local config so the UI is functional
          const existing = get().shareConfigs[sessionGuid];
          if (!existing) {
            const fallbackToken = crypto.randomUUID();
            const defaultConfig: SessionShareConfig = {
              id: 0,
              shareMode: "private",
              shareToken: fallbackToken,
              defaultPermission: "read",
              shareUrl: null,
              users: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            set((state) => ({
              shareConfigs: {
                ...state.shareConfigs,
                [sessionGuid]: defaultConfig,
              },
            }));
            return defaultConfig;
          }
          return existing;
        } finally {
          set((state) => {
            const next = new Set(state.loadingShares);
            next.delete(sessionGuid);
            return { loadingShares: next };
          });
        }
      },

      updateShareMode: async (
        sessionGuid,
        shareMode,
        defaultPermission,
        getToken,
      ) => {
        // Optimistic update: apply locally immediately so UI reflects the change
        set((state) => ({
          shareConfigs: {
            ...state.shareConfigs,
            [sessionGuid]: {
              ...state.shareConfigs[sessionGuid],
              shareMode,
              defaultPermission,
              shareUrl:
                shareMode !== "private" &&
                state.shareConfigs[sessionGuid]?.shareToken
                  ? `${window.location.origin}/shared/${state.shareConfigs[sessionGuid].shareToken}`
                  : null,
            },
          },
        }));
        // Then sync with backend
        try {
          const data = await apiFetch(
            `/api/sessions/${sessionGuid}/share`,
            getToken,
            {
              method: "PUT",
              body: JSON.stringify({ shareMode, defaultPermission }),
            },
          );
          set((state) => ({
            shareConfigs: {
              ...state.shareConfigs,
              [sessionGuid]: {
                ...state.shareConfigs[sessionGuid],
                ...data.share,
              },
            },
          }));
        } catch (err) {
          console.error("Failed to sync share mode with server:", err);
          // Keep the optimistic local state
        }
      },

      addSharedUser: async (sessionGuid, email, permission, getToken) => {
        try {
          const data = await apiFetch(
            `/api/sessions/${sessionGuid}/share/users`,
            getToken,
            {
              method: "POST",
              body: JSON.stringify({ email, permission }),
            },
          );
          // Refresh share config to get updated user list
          await get().fetchShareConfig(sessionGuid, getToken);
          return data.user;
        } catch (err) {
          console.error("Failed to add shared user:", err);
          return null;
        }
      },

      updateUserPermission: async (
        sessionGuid,
        userId,
        permission,
        getToken,
      ) => {
        await apiFetch(
          `/api/sessions/${sessionGuid}/share/users/${userId}`,
          getToken,
          {
            method: "PUT",
            body: JSON.stringify({ permission }),
          },
        );
        // Update local state
        set((state) => {
          const config = state.shareConfigs[sessionGuid];
          if (!config) return state;
          return {
            shareConfigs: {
              ...state.shareConfigs,
              [sessionGuid]: {
                ...config,
                users: config.users.map((u) =>
                  u.userId === userId ? { ...u, permission } : u,
                ),
              },
            },
          };
        });
      },

      removeSharedUser: async (sessionGuid, userId, getToken) => {
        await apiFetch(
          `/api/sessions/${sessionGuid}/share/users/${userId}`,
          getToken,
          { method: "DELETE" },
        );
        set((state) => {
          const config = state.shareConfigs[sessionGuid];
          if (!config) return state;
          return {
            shareConfigs: {
              ...state.shareConfigs,
              [sessionGuid]: {
                ...config,
                users: config.users.filter((u) => u.userId !== userId),
              },
            },
          };
        });
      },

      removeSharing: async (sessionGuid, getToken) => {
        await apiFetch(`/api/sessions/${sessionGuid}/share`, getToken, {
          method: "DELETE",
        });
        set((state) => {
          const next = { ...state.shareConfigs };
          delete next[sessionGuid];
          return { shareConfigs: next };
        });
      },

      fetchSharedWithMe: async (getToken) => {
        try {
          const data = await apiFetch(`/api/sessions/shared-with-me`, getToken);
          set({ sharedWithMe: data.sessions });
        } catch (err) {
          console.error("Failed to fetch shared sessions:", err);
        }
      },

      openShareDialog: (sessionId) => {
        set({ shareDialogSessionId: sessionId });
      },

      closeShareDialog: () => {
        set({ shareDialogSessionId: null });
      },

      getSessionShareMode: (sessionGuid) => {
        return get().shareConfigs[sessionGuid]?.shareMode || "private";
      },
    }),
    {
      name: "quanta-sharing-storage",
      partialize: (state) => ({
        shareConfigs: state.shareConfigs,
        sharedWithMe: state.sharedWithMe,
      }),
    },
  ),
);
